import type {
  ExperimentDetail,
  ExperimentInsightsData,
  ExperimentInsightsEvidenceTrace,
  ExperimentInsightsGeneralInsight,
  ExperimentInsightsResearchQuestionResult,
  PrototypeDetailResponse,
} from "../api/experimentsApi";
import type {
  FeedbackFlow,
  FeedbackFlowQuestion,
  FeedbackFlowQuestionInputType,
} from "../types/experiment.types";

export interface ReportSupportingResponse {
  response_id?: string;
  user_name?: string | null;
  title?: string | null;
  description: string;
  question: string | null;
}

export interface ReportDecisionFramework {
  decision_label: string;
  pursue_probability: number;
  pivot_probability: number;
  pause_probability: number;
  decision_description: string;
  supporting_responses: ReportSupportingResponse[];
}

export interface ReportExperimentMetrics {
  outcome_summary: number | null;
  responses: number;
  completion_rate: number | null;
  average_time_to_complete: string | null;
  insufficient_evidence_rq_count: number;
}

export interface ReportInterviewQuestion {
  question_id: string;
  question: string;
  screen_label: string;
  type: string | null;
  completion_rate: number | null;
}

export interface ReportResearchQuestion {
  rq_id: string;
  research_question: string;
  answer_label: string;
  sentiment_label: "positive" | "negative" | "neutral";
  date_label: string | null;
  description: string | null;
  ai_reasoning: string | null;
  supporting_responses: ReportSupportingResponse[];
  interview_questions: ReportInterviewQuestion[];
}

export interface ReportAdditionalInsight {
  insight_id: string;
  title: string;
  date_label: string | null;
  description: string;
  sentiment: "positive" | "neutral" | "negative";
  ai_reasoning: string;
  supporting_responses: ReportSupportingResponse[];
}

export interface ExperimentReportResultsModel {
  status: "success" | "error" | "pending";
  goal: string;
  decision_framework: ReportDecisionFramework;
  experiment_metrics: ReportExperimentMetrics;
  research_questions: ReportResearchQuestion[];
  additional_insights: ReportAdditionalInsight[];
}

export interface ExperimentReportHeaderModel {
  title: string;
  ownerName: string;
  ownerInitials: string;
  statusLabel: string;
  launchedDateLabel: string | null;
  completedDateLabel: string | null;
}

export interface ExperimentReportDetailsModel {
  businessContext: {
    experimentName: string;
    businessGoal: string;
    targetAudience: string;
  };
  researchFoundation: {
    experimentType: string;
    researchObjective: string;
    coreResearchQuestions: string[];
    primaryHypothesis: string;
    keyAssumptions: string[];
    successSignalQualitative: string;
    successSignalQuantitative: string;
  };
  prototypeAndTags: {
    screens: number;
    idea: string;
    taggedScreens: number;
    totalQuestions: number;
  };
}

export interface ExperimentReportViewModel {
  header: ExperimentReportHeaderModel;
  results: ExperimentReportResultsModel;
  details: ExperimentReportDetailsModel;
}

const trimValue = (value?: string | null): string => value?.trim() ?? "";

const normalizeInsightsStatus = (
  status?: string | null,
): string => {
  const normalized = String(status ?? "").trim().toLowerCase();
  switch (normalized) {
    case "notgenerated":
    case "not_generated":
    case "not generated":
      return "notgenerated";
    case "generating":
    case "pending":
    case "queued":
    case "running":
    case "processing":
    case "in_progress":
      return "generating";
    case "generated":
    case "succeeded":
    case "success":
    case "completed":
    case "complete":
      return "generated";
    default:
      return normalized;
  }
};

const formatInsightsStatusLabel = (
  status?: string | null,
): string => {
  switch (normalizeInsightsStatus(status)) {
    case "notgenerated":
      return "NOT GENERATED";
    case "generating":
      return "GENERATING";
    case "generated":
      return "GENERATED";
    default:
      return trimValue(String(status ?? "")).toUpperCase().replace(/_/g, " ");
  }
};

const toTitleCase = (value: string): string =>
  value
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const formatDateLabel = (value?: string | null): string | null => {
  const trimmed = trimValue(value);
  if (!trimmed) {
    return null;
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const getInitials = (name?: string | null): string => {
  const parts = trimValue(name).split(/\s+/).filter(Boolean).slice(0, 2);

  if (parts.length === 0) {
    return "FA";
  }

  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
};

const mapExperimentTypeLabel = (type?: number): string => {
  switch (type) {
    case 1:
      return "Discovery";
    case 3:
      return "Optimization";
    default:
      return "Validation";
  }
};

const mapHeaderStatusLabel = (
  experiment?: ExperimentDetail | null,
  insights?: ExperimentInsightsData | null,
): string => {
  const insightsStatusLabel = formatInsightsStatusLabel(insights?.status);
  if (insightsStatusLabel) {
    return insightsStatusLabel;
  }

  const status = experiment?.status;
  if (typeof status === "number") {
    switch (status) {
      case 1:
        return "VALIDATION";
      case 2:
        return "DEVELOPMENT";
      case 3:
        return "LAUNCHED";
      default:
        return "IDEATION";
    }
  }

  return trimValue(String(status ?? "")).toUpperCase() || "PENDING";
};

const mapReportStatus = (
  status?: string | null,
): "success" | "error" | "pending" => {
  switch (normalizeInsightsStatus(status)) {
    case "":
    case "notgenerated":
    case "generating":
      return "pending";
    case "generated":
    case "ok":
      return "success";
    default:
      return "error";
  }
};

const mapDecisionLabel = (value?: string | null): string =>
  trimValue(value) ? toTitleCase(trimValue(value)) : "Unknown";

const mapAnswerSentiment = (
  answerLabel?: string | null,
): "positive" | "negative" | "neutral" => {
  switch (trimValue(answerLabel).toLowerCase()) {
    case "supported":
      return "positive";
    case "not_supported":
      return "negative";
    default:
      return "neutral";
  }
};

const mapQuestionType = (
  type?: FeedbackFlowQuestionInputType,
): string | null => {
  switch (type) {
    case "multiple_choice":
      return "multiple-choice";
    case "checkbox":
      return "multiple-select";
    case "likert_scale":
      return "likert";
    case "text":
      return "free-text";
    case "speech_to_text":
      return "voice";
    default:
      return null;
  }
};

const normalizeRoute = (value?: string | null): string => {
  const trimmed = trimValue(value);
  if (!trimmed) {
    return "/";
  }

  try {
    const parsed = /^https?:\/\//i.test(trimmed)
      ? new URL(trimmed)
      : new URL(trimmed, "https://falcon.local");
    const pathname = parsed.pathname || "/";
    const normalized = pathname.replace(/\/+$/, "");
    return normalized || "/";
  } catch {
    const withoutQuery = trimmed.split("?")[0]?.split("#")[0] ?? "/";
    const normalized = withoutQuery.startsWith("/")
      ? withoutQuery
      : `/${withoutQuery}`;
    const withoutTrailingSlash = normalized.replace(/\/+$/, "");
    return withoutTrailingSlash || "/";
  }
};

const formatScreenName = (route: string): string => {
  if (route === "/") {
    return "Welcome";
  }

  const label = route
    .split("/")
    .filter(Boolean)
    .map((segment) =>
      segment
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
    )
    .join(" ");

  return label || "Untitled Screen";
};

const buildPrototypeScreenLabels = (
  questions: FeedbackFlowQuestion[],
): Map<string, string> => {
  const routeToLabel = new Map<string, string>();
  let screenIndex = 1;

  questions.forEach((question) => {
    const route = normalizeRoute(question.taggedTarget?.route);

    if (!routeToLabel.has(route)) {
      routeToLabel.set(
        route,
        `Screen ${screenIndex} — ${formatScreenName(route)}`,
      );
      screenIndex += 1;
    }
  });

  return routeToLabel;
};

const buildQuestionSupportingResponses = (
  evidenceTrace?: ExperimentInsightsEvidenceTrace[] | null,
): ReportSupportingResponse[] =>
  (evidenceTrace ?? []).flatMap((trace, traceIndex) => {
    const userName =
      trimValue(trace.participant_name) ||
      trimValue(trace.user_name) ||
      `Participant ${traceIndex + 1}`;

    return (trace.exemplar_quotes ?? [])
      .map((quote) => trimValue(quote))
      .filter(Boolean)
      .map((quote) => ({
        response_id: crypto.randomUUID(),
        user_name: userName,
        description: quote,
        question: null,
      }));
  });

const buildDecisionSupportingResponses = (
  actions?: string[] | null,
): ReportSupportingResponse[] =>
  (actions ?? [])
    .map((action) => trimValue(action))
    .filter(Boolean)
    .map((action) => ({
      response_id: crypto.randomUUID(),
      title: action,
      description: action,
      question: null,
    }));

const buildAdditionalInsightDescription = (
  insight: ExperimentInsightsGeneralInsight,
): string => {
  const summary = trimValue(insight.evidence?.summary);
  if (summary) {
    return summary;
  }

  const title = trimValue(insight.title);
  const supportScore = insight.support_score;
  const supportLabel =
    typeof supportScore === "number"
      ? `${Math.round(supportScore * 100)}% support`
      : "support score unavailable";
  const confidenceLabel = trimValue(insight.confidence_bucket)
    ? toTitleCase(trimValue(insight.confidence_bucket))
    : null;

  return [title, confidenceLabel, supportLabel].filter(Boolean).join(" • ");
};

const mapAdditionalInsightSentiment = (
  supportScore?: number,
): "positive" | "neutral" | "negative" => {
  if (typeof supportScore !== "number") {
    return "neutral";
  }

  if (supportScore > 0.6) {
    return "positive";
  }

  if (supportScore >= 0.4) {
    return "neutral";
  }

  return "negative";
};

const buildGeneralInsightSupportingResponses = (
  insight: ExperimentInsightsGeneralInsight,
): ReportSupportingResponse[] =>
  (insight.evidence?.quotes ?? [])
    .map((quote) => trimValue(quote))
    .filter(Boolean)
    .map((quote, index) => ({
      response_id: crypto.randomUUID(),
      user_name: `Participant ${index + 1}`,
      description: quote,
      question: null,
    }));

const getPremiseValues = (
  experiment: ExperimentDetail | null | undefined,
  type: number,
): string[] =>
  (experiment?.premises ?? [])
    .filter((premise) => premise.type === type)
    .map((premise) => trimValue(premise.value))
    .filter(Boolean);

const getPrototypeQuestions = (
  feedbackFlow?: FeedbackFlow | null,
): FeedbackFlowQuestion[] =>
  (feedbackFlow?.questions ?? []).filter(
    (question) => question.section === "prototype",
  );

const buildInterviewQuestions = (
  allQuestions: FeedbackFlowQuestion[],
  screenLabels: Map<string, string>,
  researchQuestionResult: ExperimentInsightsResearchQuestionResult,
): ReportInterviewQuestion[] => {
  const researchQuestionId = trimValue(researchQuestionResult.rq_id);

  return allQuestions
    .filter((question) => trimValue(question.premiseId) === researchQuestionId)
    .map((question) => ({
      question_id: trimValue(question.id) || crypto.randomUUID(),
      question: trimValue(question.question) || "Untitled question",
      screen_label:
        screenLabels.get(normalizeRoute(question.taggedTarget?.route)) ||
        "Unmapped Screen",
      type: mapQuestionType(question.inputType),
      completion_rate: null,
    }));
};

export const buildExperimentReportViewModel = ({
  displayName,
  experiment,
  insights,
  feedbackFlow,
  prototype,
}: {
  displayName?: string;
  experiment?: ExperimentDetail | null;
  insights?: ExperimentInsightsData | null;
  feedbackFlow?: FeedbackFlow | null;
  prototype?: PrototypeDetailResponse | null;
}): ExperimentReportViewModel => {
  const prototypeQuestions = getPrototypeQuestions(feedbackFlow);
  const prototypeScreenLabels = buildPrototypeScreenLabels(prototypeQuestions);
  const taggedRoutes = new Set(
    prototypeQuestions
      .map((question) => trimValue(question.taggedTarget?.route))
      .filter(Boolean),
  );
  const detailsResearchQuestions = getPremiseValues(experiment, 1);
  const detailsAssumptions = getPremiseValues(experiment, 2);
  const detailsSuccessSignals = getPremiseValues(experiment, 3);
  const title =
    trimValue(displayName) ||
    trimValue(experiment?.name) ||
    "Untitled Experiment";
  const ownerName = trimValue(experiment?.owner) || "Falcon User";

  return {
    header: {
      title,
      ownerName,
      ownerInitials: getInitials(ownerName),
      statusLabel: mapHeaderStatusLabel(experiment, insights),
      launchedDateLabel: formatDateLabel(experiment?.createdAt),
      completedDateLabel:
        formatDateLabel(insights?.timestamp) ||
        formatDateLabel(experiment?.updatedAt),
    },
    results: {
      status: mapReportStatus(insights?.status),
      goal: trimValue(experiment?.goal),
      decision_framework: {
        decision_label: mapDecisionLabel(
          insights?.final_decision?.decision_label,
        ),
        pursue_probability: insights?.final_decision?.pursue_probability ?? 0,
        pivot_probability: insights?.final_decision?.pivot_probability ?? 0,
        pause_probability: insights?.final_decision?.pause_probability ?? 0,
        decision_description:
          trimValue(insights?.final_decision?.verbalized_decision) ||
          trimValue(insights?.final_decision?.rationale),
        supporting_responses: buildDecisionSupportingResponses(
          insights?.final_decision?.recommended_actions,
        ),
      },
      experiment_metrics: {
        outcome_summary: insights?.final_decision?.impact_score ?? null,
        responses: insights?.analysis_summary?.participant_count ?? 0,
        completion_rate: null,
        average_time_to_complete: null,
        insufficient_evidence_rq_count:
          insights?.analysis_summary?.insufficient_evidence_rq_count ?? 0,
      },
      research_questions: (insights?.core_research_question_results ?? []).map(
        (researchQuestionResult) => ({
          rq_id: trimValue(researchQuestionResult.rq_id) || crypto.randomUUID(),
          research_question:
            trimValue(researchQuestionResult.research_question) ||
            "Untitled research question",
          answer_label:
            trimValue(researchQuestionResult.answer_label) || "unknown",
          sentiment_label: mapAnswerSentiment(
            researchQuestionResult.answer_label,
          ),
          date_label:
            formatDateLabel(insights?.timestamp) ||
            formatDateLabel(experiment?.updatedAt),
          description:
            trimValue(researchQuestionResult.verbalized_answer) || null,
          ai_reasoning: trimValue(researchQuestionResult.rationale) || null,
          supporting_responses: buildQuestionSupportingResponses(
            researchQuestionResult.evidence_trace,
          ),
          interview_questions: buildInterviewQuestions(
            prototypeQuestions,
            prototypeScreenLabels,
            researchQuestionResult,
          ),
        }),
      ),
      additional_insights: (insights?.general_insights ?? []).map(
        (insight) => ({
          insight_id: trimValue(insight.insight_id) || crypto.randomUUID(),
          title: trimValue(insight.title) || "Untitled insight",
          date_label:
            formatDateLabel(insights?.timestamp) ||
            formatDateLabel(experiment?.updatedAt),
          description: buildAdditionalInsightDescription(insight),
          sentiment: mapAdditionalInsightSentiment(insight.support_score),
          ai_reasoning: [
            trimValue(insight.type)
              ? toTitleCase(trimValue(insight.type))
              : null,
            trimValue(insight.confidence_bucket)
              ? `${toTitleCase(trimValue(insight.confidence_bucket))} confidence`
              : null,
            typeof insight.support_score === "number"
              ? `${Math.round(insight.support_score * 100)}% support`
              : null,
          ]
            .filter(Boolean)
            .join(" • "),
          supporting_responses: buildGeneralInsightSupportingResponses(insight),
        }),
      ),
    },
    details: {
      businessContext: {
        experimentName: trimValue(experiment?.name) || title,
        businessGoal: trimValue(experiment?.goal),
        targetAudience: trimValue(experiment?.audience),
      },
      researchFoundation: {
        experimentType: mapExperimentTypeLabel(experiment?.type),
        researchObjective: trimValue(experiment?.foundation?.objective),
        coreResearchQuestions: detailsResearchQuestions,
        primaryHypothesis: trimValue(experiment?.foundation?.hypothesis),
        keyAssumptions: detailsAssumptions,
        successSignalQualitative: detailsSuccessSignals[0] ?? "",
        successSignalQuantitative: detailsSuccessSignals[1] ?? "",
      },
      prototypeAndTags: {
        screens: taggedRoutes.size,
        idea:
          trimValue(prototype?.summaryPlan) || trimValue(experiment?.context),
        taggedScreens: taggedRoutes.size,
        totalQuestions: feedbackFlow?.questions.length ?? 0,
      },
    },
  };
};
